<script setup lang="ts">
import { provide, ref, useSlots, useTemplateRef } from 'vue';
import { useMutationObserver } from '@vueuse/core';
import { AlignJustify, Folder, LayoutGrid, Search, X } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import {
    Empty,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from '@/components/ui/empty';
import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
} from '@/components/ui/input-group';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import useFileSearch from '@/composables/useFileSearch.ts';
import {
    useDocumentsLayoutStore,
    viewModeKey,
} from '@/stores/documents-layout-store.ts';

const slots = useSlots();
const slotContainer = useTemplateRef('slotContainer');
const hasContent = ref((slots?.default?.()?.length ?? 0) >= 1);

useMutationObserver(
    slotContainer,
    () => {
        hasContent.value =
            slotContainer.value?.querySelector(
                ':scope > :not(.empty-fallback)'
            ) !== null;
    },
    {
        childList: true,
    }
);

const documentsLayoutStore = useDocumentsLayoutStore();
const { viewMode } = storeToRefs(documentsLayoutStore);
provide(viewModeKey, viewMode);

const { query, reset, isPending } = useFileSearch();
</script>

<template>
    <div class="flex flex-col gap-y-6">
        <div class="flex justify-end gap-x-3">
            <InputGroup class="mr-auto ml-0 max-w-sm">
                <InputGroupInput
                    placeholder="Nach einem Dokument suchen"
                    v-model="query"
                />
                <InputGroupAddon>
                    <Search />
                </InputGroupAddon>
                <InputGroupAddon
                    v-if="query !== ''"
                    align="inline-end"
                >
                    <InputGroupButton
                        aria-label="Suche zurücksetzen"
                        size="icon-xs"
                        @click="reset"
                    >
                        <X />
                    </InputGroupButton>
                </InputGroupAddon>
            </InputGroup>

            <ToggleGroup
                v-model="viewMode"
                variant="outline"
                type="single"
            >
                <ToggleGroupItem value="grid">
                    <LayoutGrid />
                </ToggleGroupItem>
                <ToggleGroupItem value="list">
                    <AlignJustify />
                </ToggleGroupItem>
            </ToggleGroup>
        </div>

        <div
            ref="slotContainer"
            :class="{
                'view-grid grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-6':
                    viewMode === 'grid',
                'view-list grid gap-x-6 divide-y xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto]':
                    viewMode === 'list',
            }"
        >
            <template v-if="isPending">
                <Skeleton
                    v-for="_ in 6"
                    class="opacity-25 in-[.view-grid]:h-96 in-[.view-list]:col-span-full in-[.view-list]:my-1 in-[.view-list]:h-9"
                />
            </template>
            <template v-else>
                <slot />
                <Empty
                    class="empty-fallback col-span-full"
                    v-if="!hasContent"
                >
                    <EmptyHeader>
                        <EmptyMedia variant="icon">
                            <Folder />
                        </EmptyMedia>
                        <EmptyTitle>Keine Dokumente</EmptyTitle>
                        <EmptyDescription
                            >In dieser Ansicht stehen keine passenden Dokumente
                            bereit.</EmptyDescription
                        >
                    </EmptyHeader>
                </Empty>
            </template>
        </div>
    </div>
</template>
